import React, { useEffect, useMemo, useState } from 'react';
import { invoiceAPI } from '../../lib/api';
import StatusBadge from '../../components/ui/StatusBadge';
import { formatCurrencyFull, formatDate, formatMonth } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
    FileText,
    Loader2,
    Download,
    Plus,
    CheckCircle,
    XCircle,
    Clock,
    Trash2,
    AlertTriangle,
    DollarSign,
    CreditCard,
} from 'lucide-react';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import PaymentDetailsModal from '../../components/PaymentDetailsModal';

// Canonical status map — every backend status routes to one bucket and gets a label/colour/icon.
// Bucket is what the filter tabs use; label is what the badge renders.
const STATUS_MAP = {
    pending_finance:  { bucket: 'pending',  label: 'Pending Finance',  color: 'bg-yellow-500/20 text-yellow-500 border-yellow-500/30', icon: Clock },
    pending_founder:  { bucket: 'pending',  label: 'Pending Founder',  color: 'bg-yellow-500/20 text-yellow-500 border-yellow-500/30', icon: Clock },
    approved:         { bucket: 'pending',  label: 'Approved',         color: 'bg-blue-500/20 text-blue-500 border-blue-500/30',     icon: CheckCircle },
    paid:             { bucket: 'paid',     label: 'Paid',             color: 'bg-secondary/20 text-secondary border-secondary/30', icon: DollarSign },
    rejected:         { bucket: 'rejected', label: 'Rejected',         color: 'bg-destructive/20 text-destructive border-destructive/30', icon: XCircle },
    rejected_finance: { bucket: 'rejected', label: 'Rejected (Finance)', color: 'bg-destructive/20 text-destructive border-destructive/30', icon: XCircle },
    rejected_founder: { bucket: 'rejected', label: 'Rejected (Founder)', color: 'bg-destructive/20 text-destructive border-destructive/30', icon: XCircle },
};
const FALLBACK = { bucket: 'pending', label: 'Pending', color: 'bg-yellow-500/20 text-yellow-500 border-yellow-500/30', icon: Clock };


const REJECTED_STATUSES = new Set(['rejected', 'rejected_finance', 'rejected_founder', 'founder_rejected', 'finance_rejected']);
const IN_PROCESS_STATUSES = new Set(['pending_finance', 'pending_founder']);

const getLabelFacingNote = (invoice) => {
    const isRejected = REJECTED_STATUSES.has(invoice.status);
    if (isRejected) {
        return invoice.founder_remarks || invoice.finance_remarks || invoice.finance_notes || 'Rejected. Please review and re-submit.';
    }
    if (IN_PROCESS_STATUSES.has(invoice.status)) {
        return 'In Process';
    }
    if (invoice.status === 'approved') {
        return 'Approved / Awaiting Payment';
    }
    if (invoice.status === 'paid') {
        return invoice.payment_comment || 'Paid';
    }
    return '—';
};

const LabelInvoicesPage = () => {
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deletingId, setDeletingId] = useState(null);
    const [filter, setFilter] = useState('all');
    const [availablePeriods, setAvailablePeriods] = useState([]);
    const [paymentDetailsInvoice, setPaymentDetailsInvoice] = useState(null);

    const fetchInvoices = async () => {
        try {
            const [invRes, availRes] = await Promise.all([
                invoiceAPI.getAll(),
                invoiceAPI.getAvailablePeriods().catch(() => ({ data: [] })),
            ]);
            setInvoices(invRes.data);
            setAvailablePeriods(availRes.data || []);
        } catch (error) {
            console.error('Failed to fetch invoices:', error);
            toast.error('Failed to load invoices');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchInvoices(); }, []);

    const handleDelete = async (invoiceId) => {
        setDeletingId(invoiceId);
        try {
            await invoiceAPI.delete(invoiceId);
            toast.success('Invoice deleted');
            fetchInvoices();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete invoice');
        } finally {
            setDeletingId(null);
        }
    };

    const handleDownload = async (invoiceId) => {
        try {
            const response = await invoiceAPI.downloadAttachment(invoiceId);
            const { filename, data } = response.data;
            const byteCharacters = atob(data);
            const byteNumbers = new Array(byteCharacters.length).fill(0).map((_, i) => byteCharacters.charCodeAt(i));
            const blob = new Blob([new Uint8Array(byteNumbers)]);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to download attachment');
        }
    };

    // Bucket counts for filter tabs
    const counts = useMemo(() => {
        const c = { all: invoices.length, pending: 0, paid: 0, rejected: 0 };
        invoices.forEach(inv => {
            const m = STATUS_MAP[inv.status] || FALLBACK;
            c[m.bucket] = (c[m.bucket] || 0) + 1;
        });
        return c;
    }, [invoices]);

    const visibleInvoices = useMemo(() => {
        if (filter === 'all') return invoices;
        return invoices.filter(inv => (STATUS_MAP[inv.status] || FALLBACK).bucket === filter);
    }, [filter, invoices]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-[50vh]">
                <Loader2 className="w-8 h-8 animate-spin text-secondary" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="label-invoices-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-heading font-bold tracking-tight">Invoices</h1>
                    <p className="text-muted-foreground">View and manage your invoices</p>
                </div>
                <Button asChild className="bg-secondary text-secondary-foreground hover:bg-secondary/90">
                    <Link to="/label/invoices/create" data-testid="create-invoice-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Create Invoice
                    </Link>
                </Button>
            </div>

            {/* Available to Invoice (un-invoiced revenue periods) */}
            {availablePeriods.length > 0 && (
                <Card className="bg-card/50 border-amber-500/30" data-testid="available-to-invoice-section">
                    <CardHeader>
                        <CardTitle className="font-heading flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-amber-500" />
                            Available to Invoice
                            <span className="text-xs font-normal text-muted-foreground">
                                ({availablePeriods.length} period{availablePeriods.length === 1 ? '' : 's'})
                            </span>
                        </CardTitle>
                        <p className="text-sm text-muted-foreground mt-1">
                            You have payable revenue for these reporting months but haven't raised an invoice yet.
                        </p>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {availablePeriods.map((p) => (
                                <div
                                    key={p.month}
                                    className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 flex flex-col gap-2"
                                    data-testid={`available-period-${p.month}`}
                                >
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Reporting Month</div>
                                            <div className="text-base font-heading font-bold">{formatMonth(p.month)}</div>
                                        </div>
                                        <Badge variant="outline" className="border-amber-500/40 text-amber-500 text-[10px]">
                                            Not Invoiced
                                        </Badge>
                                    </div>
                                    <div>
                                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Payable</div>
                                        <div className="text-lg font-bold text-amber-500">
                                            {formatCurrencyFull(p.amount, 'INR')}
                                        </div>
                                    </div>
                                    <Button
                                        asChild
                                        size="sm"
                                        className="bg-secondary text-secondary-foreground hover:bg-secondary/90 mt-1"
                                        data-testid={`raise-invoice-${p.month}`}
                                    >
                                        <Link to={`/label/invoices/create?month=${encodeURIComponent(p.month)}`}>
                                            <Plus className="w-3.5 h-3.5 mr-1.5" /> Raise Invoice
                                        </Link>
                                    </Button>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Filter Tabs */}
            <Tabs value={filter} onValueChange={setFilter} className="w-full">
                <TabsList data-testid="label-invoice-filter-tabs">
                    <TabsTrigger value="all"      data-testid="tab-all">All <span className="ml-1 text-xs opacity-60">({counts.all})</span></TabsTrigger>
                    <TabsTrigger value="pending"  data-testid="tab-pending">Pending <span className="ml-1 text-xs opacity-60">({counts.pending || 0})</span></TabsTrigger>
                    <TabsTrigger value="paid"     data-testid="tab-paid">Paid <span className="ml-1 text-xs opacity-60">({counts.paid || 0})</span></TabsTrigger>
                    <TabsTrigger value="rejected" data-testid="tab-rejected">Rejected <span className="ml-1 text-xs opacity-60">({counts.rejected || 0})</span></TabsTrigger>
                </TabsList>
            </Tabs>

            {/* Invoices Table */}
            <Card className="bg-card/50 border-border/40">
                <CardHeader>
                    <CardTitle className="font-heading flex items-center gap-2">
                        <FileText className="w-5 h-5 text-secondary" />
                        Your Invoices ({visibleInvoices.length})
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="rounded-lg border border-border/40 overflow-hidden">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-muted/20 hover:bg-muted/20">
                                    <TableHead className="font-mono text-xs uppercase tracking-wider">Invoice #</TableHead>
                                    <TableHead className="font-mono text-xs uppercase tracking-wider">Period</TableHead>
                                    <TableHead className="font-mono text-xs uppercase tracking-wider text-right">Amount</TableHead>
                                    <TableHead className="font-mono text-xs uppercase tracking-wider">Status</TableHead>
                                    <TableHead className="font-mono text-xs uppercase tracking-wider">Reason / Notes</TableHead>
                                    <TableHead className="font-mono text-xs uppercase tracking-wider">Attachment</TableHead>
                                    <TableHead className="font-mono text-xs uppercase tracking-wider">Created</TableHead>
                                    <TableHead className="font-mono text-xs uppercase tracking-wider text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {visibleInvoices.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} className="text-center py-12">
                                            <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                                            <p className="text-muted-foreground mb-4">
                                                {filter === 'all' ? 'No invoices yet' : `No ${filter} invoices`}
                                            </p>
                                            {filter === 'all' && (
                                                <Button asChild variant="outline">
                                                    <Link to="/label/invoices/create">Create your first invoice</Link>
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    visibleInvoices.map((invoice) => {
                                        const status = STATUS_MAP[invoice.status] || FALLBACK;
                                        const StatusIcon = status.icon;
                                        const isRejected = status.bucket === 'rejected';
                                        const labelNote = getLabelFacingNote(invoice);
                                        const showNote = labelNote && labelNote !== '—';

                                        return (
                                            <TableRow key={invoice.id} className="hover:bg-muted/10 border-border/40" data-testid={`invoice-row-${invoice.id}`}>
                                                <TableCell className="font-mono text-sm">{invoice.invoice_number}</TableCell>
                                                <TableCell>{formatMonth(invoice.month)}</TableCell>
                                                <TableCell className="text-right font-mono font-medium text-secondary">
                                                    {formatCurrencyFull(invoice.amount, invoice.currency)}
                                                </TableCell>
                                                <TableCell>
                                                    <StatusBadge status={invoice.status} />
                                                </TableCell>
                                                <TableCell className="max-w-[240px]">
                                                    {showNote ? (
                                                        <div
                                                            className={`flex items-start gap-1.5 text-xs ${isRejected ? 'text-destructive' : 'text-muted-foreground'}`}
                                                            data-testid={`invoice-reason-${invoice.id}`}
                                                        >
                                                            {isRejected && <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />}
                                                            <span className="whitespace-pre-wrap break-words">{labelNote}</span>
                                                        </div>
                                                    ) : (
                                                        <span className="text-muted-foreground text-xs">—</span>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {invoice.attachment_filename ? (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleDownload(invoice.id)}
                                                            className="text-secondary"
                                                            data-testid={`download-${invoice.id}`}
                                                        >
                                                            <Download className="w-4 h-4 mr-1" />
                                                            Download
                                                        </Button>
                                                    ) : (
                                                        <span className="text-muted-foreground text-sm">-</span>
                                                    )}
                                                </TableCell>
                                                <TableCell className="text-muted-foreground text-sm">
                                                    {formatDate(invoice.created_at)}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    {invoice.status === 'paid' ? (
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => setPaymentDetailsInvoice(invoice)}
                                                            className="border-green-500/30 text-green-600 hover:text-green-600 hover:bg-green-500/10"
                                                            data-testid={`payment-details-${invoice.id}`}
                                                        >
                                                            <CreditCard className="w-4 h-4 mr-1" />
                                                            Payment
                                                        </Button>
                                                    ) : invoice.status === 'pending_finance' ? (
                                                        <AlertDialog>
                                                            <AlertDialogTrigger asChild>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                                                    data-testid={`delete-invoice-${invoice.id}`}
                                                                    disabled={deletingId === invoice.id}
                                                                >
                                                                    <Trash2 className="w-4 h-4" />
                                                                </Button>
                                                            </AlertDialogTrigger>
                                                            <AlertDialogContent>
                                                                <AlertDialogHeader>
                                                                    <AlertDialogTitle>Delete invoice {invoice.invoice_number}?</AlertDialogTitle>
                                                                    <AlertDialogDescription>
                                                                        This will permanently remove the invoice. You can only delete invoices that haven't been reviewed by Finance yet. This action cannot be undone.
                                                                    </AlertDialogDescription>
                                                                </AlertDialogHeader>
                                                                <AlertDialogFooter>
                                                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                                                    <AlertDialogAction
                                                                        onClick={() => handleDelete(invoice.id)}
                                                                        className="bg-destructive hover:bg-destructive/90"
                                                                        data-testid={`confirm-delete-${invoice.id}`}
                                                                    >
                                                                        Delete
                                                                    </AlertDialogAction>
                                                                </AlertDialogFooter>
                                                            </AlertDialogContent>
                                                        </AlertDialog>
                                                    ) : (
                                                        <span className="text-muted-foreground text-xs">—</span>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>

            <PaymentDetailsModal
                invoice={paymentDetailsInvoice}
                open={!!paymentDetailsInvoice}
                onOpenChange={(isOpen) => {
                    if (!isOpen) setPaymentDetailsInvoice(null);
                }}
            />
        </div>
    );
};

export default LabelInvoicesPage;
